import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { ContentListDocumentMapping, CTADocumentMapping } from '@/utils/mappings';
import {
  resolveColorName,
  resolveDate,
  resolveDocument,
  resolveSpacingSize,
  resolveTheme,
} from '@/utils/resolvers';
import React, { memo, useMemo, useRef } from 'react';
import News from './News';
import { onDatalayer } from './News.datalayer';

const resolveContentDocument = ({ contentList = [], ...props }) => {
  const list = contentList.map((item: any) => {
    return {
      ...item,
      cardHeading: item?.cardHeading || item.title,
      cardPublicationDate:
        item?.cardPublicationDate || resolveDate({ rawVal: item?.publicationDate }),
    };
  });

  return {
    contentList: list,
    ...props,
  };
};

const MappedNews = memo(
  ({
    contentButtons,
    presentation,
    introContent,
    curatedListContent,
    featuredContent,
    rightRailContent,
    isPreview,
  }: any) => {
    const { topSpacing, bottomSpacing, containerBackgroundColor } = presentation;
    const containerProps = useMemo(
      () => ({
        topSpacing,
        bottomSpacing,
        topBackgroundColor: containerBackgroundColor,
        bottomBackgroundColor: containerBackgroundColor,
      }),
      [bottomSpacing, containerBackgroundColor, topSpacing],
    );
    const theme = useMemo(
      () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
      [containerBackgroundColor],
    );
    const curatedList = useMemo(
      () => resolveContentDocument(curatedListContent),
      [curatedListContent],
    );
    const ContentContainerGTM = withGTM({ Component: ContentContainer, onDatalayer });

    return (
      <ContentContainerGTM
        {...containerProps}
        datalayerPosition="news"
        datalayerVariant={presentation?.datalayerVariant}
      >
        <MuiContainer className="large">
          <News
            {...presentation}
            topSpacing={topSpacing}
            introContent={introContent}
            curatedList={curatedList}
            featuredContent={featuredContent}
            rightRailContent={rightRailContent}
            contentButtons={contentButtons}
            theme={theme}
            isSticky={Boolean(isPreview)}
          />
        </MuiContainer>
      </ContentContainerGTM>
    );
  },
);

const resolveRightRailContent = ({ rawVal, page, pageModels }: any) => {
  return rawVal?.map((doc: any) => resolveDocument({ rawVal: doc, page, pageModels }));
};

const NewsCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    introductoryDocument: ['introContent', CTADocumentMapping],
    curatedListDocument: ['curatedListContent', ContentListDocumentMapping],
    featuredDocument: ['featuredContent', resolveDocument],
    rightRailContent: [null, resolveRightRailContent],
    componentParameterMap: [
      'presentation',
      {
        'container-background-color': ['containerBackgroundColor', resolveColorName],
        'top-spacing': ['topSpacing', resolveSpacingSize],
        'bottom-spacing': ['bottomSpacing', resolveSpacingSize],
        'variant-label': 'datalayerVariant',
      },
    ],
  });

  const requiredFields = useRef({
    featuredDocument: '<b>Featured Content</b> required',
  });

  const contentButtonNames = useRef({
    curatedDocument: 'curatedList',
    featuredDocument: 'featuredContent',
    document: 'introContent',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedNews}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default NewsCMSWrapper;
